import { Table } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDeleteSubscriptionMutation, useGetAllSubscriptionsQuery, useGetSubscriptionsCountQuery } from "api/subscriptionApi";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Column, Row, useTable } from "react-table";

import { useGetCsrfTokenQuery } from "src/api";
import {
    Button,
    ButtonVariant,
    Flex,
    TableBody,
    TableHeader,
    TableLayout,
    TablePagination,
    TextInput,
    useTablePageSize,
} from "src/components";
import { showSuccessNotification } from "src/notifications";
import type { UserSubscription } from "src/types";
import { formatDateOrFallback } from "src/utils";
import { DEFAULT_PAGE, DEFAULT_SIZE, DEFAULT_SORT, SUBSCRIPTIONS_PAGE_SIZE_STORAGE_KEY } from "../constants";
import type { SortableColumn, TableSort } from "../sort";
import { DEFAULT_SORT_DIRECTION_BY_COLUMN, getNextSort } from "../sort";
import { SubscribersCounter } from "./SubscribersCounter";
import { UserTableHeader } from "./UserTableHeader";

export const UserTable: FC = () => {
    const { t } = useTranslation();

    const [page, setPage] = useState<number>(DEFAULT_PAGE);
    const [size, setSize] = useTablePageSize(SUBSCRIPTIONS_PAGE_SIZE_STORAGE_KEY, DEFAULT_SIZE);
    const [sort, setSortState] = useState<TableSort>({
        column: DEFAULT_SORT,
        direction: DEFAULT_SORT_DIRECTION_BY_COLUMN[DEFAULT_SORT],
    });
    const [search, setSearch] = useState("");

    const { data: csrfToken } = useGetCsrfTokenQuery();

    const subsQueryArgs = useMemo(
        () =>
            csrfToken?.token
                ? { page, size, sort: sort.column, direction: sort.direction, search, csrfToken: csrfToken?.token }
                : skipToken,
        [page, size, sort, search, csrfToken],
    );
    const { data, currentData, isFetching } = useGetAllSubscriptionsQuery(subsQueryArgs, {
        pollingInterval: 5000,
        refetchOnMountOrArgChange: true,
    });
    const { data: countData } = useGetSubscriptionsCountQuery();
    const [deleteSubscription] = useDeleteSubscriptionMutation();

    const isFetchingNewData = data === undefined || (isFetching && currentData !== data);

    const onSort = useCallback((column: SortableColumn) => {
        setSortState((current) => getNextSort(current, column));
        setPage(DEFAULT_PAGE);
    }, []);

    const nextPage = useCallback(() => setPage(page + 1), [page]);
    const previousPage = useCallback(() => setPage(page - 1), [page]);

    const onSearch = useCallback((value: string) => {
        setSearch(value);
        setPage(DEFAULT_PAGE);
    }, []);

    const onSetPageSize = useCallback(
        (pageSize: number) => {
            setSize(pageSize);
            setPage(DEFAULT_PAGE);
        },
        [setSize],
    );

    const onDeleteSubscription = useCallback(
        async (id: number) => {
            try {
                await deleteSubscription({
                    csrfToken: csrfToken!.token!,
                    id,
                }).unwrap();
                showSuccessNotification({
                    title: t("userManagement.notification.title"),
                    message: t("userManagement.notification.message"),
                });
            } catch (e) {
                // handled by global error handling
            }
        },
        [csrfToken, deleteSubscription, t],
    );

    const columns: Column<UserSubscription>[] = useMemo(() => {
        return [
            {
                accessor: "username",
                Header: <UserTableHeader column="username" setSort={onSort} sort={sort} title={t("userManagement.user")} />,
                minWidth: 150,
            },
            {
                accessor: "email",
                Header: <UserTableHeader column="email" setSort={onSort} sort={sort} title={t("userManagement.email")} />,
                minWidth: 150,
            },
            {
                accessor: "phoneNumber",
                Header: <UserTableHeader column="phoneNumber" setSort={onSort} sort={sort} title={t("userManagement.phone")} />,
                minWidth: 120,
            },
            {
                accessor: "createdAt",
                Header: <UserTableHeader column="createdAt" setSort={onSort} sort={sort} title={t("userManagement.createdAt")} />,
                Cell: ({ row }: { row: Row<UserSubscription> }) => <>{formatDateOrFallback(row.original.createdAt, t("notAvailable"))}</>,
                minWidth: 140,
            },
            {
                accessor: "expirationDate",
                Header: <UserTableHeader column="expirationDate" setSort={onSort} sort={sort} title={t("userManagement.expirationDate")} />,
                Cell: ({ row }: { row: Row<UserSubscription> }) => (
                    <>{formatDateOrFallback(row.original.expirationDate, t("notAvailable"))}</>
                ),
                minWidth: 140,
            },
            {
                id: "id",
                Cell: ({ row }: { row: Row<UserSubscription> }) => (
                    <Button onClick={() => onDeleteSubscription(row.original.id)} variant={ButtonVariant.Outline}>
                        {t("userManagement.unsubscription")}
                    </Button>
                ),
            },
        ];
    }, [t, sort, onDeleteSubscription, onSort]);

    const options = useMemo(
        () => ({
            columns,
            data: data?.content || [],
        }),
        [data, columns],
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<UserSubscription>(options);

    return (
        <>
            <Flex flexDirection="row" justifyContent="space-between" alignItems="flex-end" gap="8px">
                <TextInput label={t("userManagement.search")} value={search} onChange={({ target: { value } }) => onSearch(value)} />
                <SubscribersCounter count={countData?.count} />
            </Flex>
            <TableLayout>
                <Table {...getTableProps()}>
                    <TableHeader headerGroups={headerGroups} />
                    <TableBody<UserSubscription>
                        noDataMessage={t("userManagement.userNotFound")}
                        getTableBodyProps={getTableBodyProps}
                        rows={rows}
                        prepareRow={prepareRow}
                        isLoading={isFetchingNewData}
                    />
                </Table>
                <TablePagination
                    totalPages={data?.totalPages || 0}
                    pageSize={size}
                    setPageSize={onSetPageSize}
                    pageIndex={page}
                    nextPage={nextPage}
                    previousPage={previousPage}
                />
            </TableLayout>
        </>
    );
};
